import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage, formatApiErrorDetail } from "@/lib/api";

export default function ImageUploader({
  value,
  onChange,
  label = "Image",
  testIdPrefix = "uploader",
  helper = "Upload a JPG, PNG, WEBP, or GIF (max 8MB) — or paste an image URL below.",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be 8MB or smaller.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onChange?.("");

  return (
    <div data-testid={`${testIdPrefix}-root`}>
      <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
        {label}
      </label>

      {value ? (
        <div className="relative inline-block">
          <img
            src={resolveImage(value)}
            alt="preview"
            data-testid={`${testIdPrefix}-preview`}
            className="max-h-72 object-cover border border-rule"
          />
          <button
            type="button"
            onClick={clear}
            data-testid={`${testIdPrefix}-clear`}
            className="absolute top-2 right-2 bg-paper border border-ink text-ink hover:bg-ink hover:text-paper p-1.5"
            title="Remove image"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div
          className="border border-dashed border-rule bg-paper-2/40 p-8 text-center"
          data-testid={`${testIdPrefix}-empty`}
        >
          <p className="font-serif-display text-lg italic text-ink-soft mb-3">
            No image yet
          </p>
          <p className="text-xs text-ink-soft mb-5">{helper}</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      <div className="flex flex-wrap items-center gap-3 mt-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-upload-btn`}
          className="inline-flex items-center gap-2 px-4 py-2 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 className="animate-spin" size={14} /> Uploading…
            </>
          ) : (
            <>
              <Upload size={14} /> {value ? "Replace image" : "Upload image"}
            </>
          )}
        </button>

        <button
          type="button"
          onClick={() => setShowUrl((v) => !v)}
          data-testid={`${testIdPrefix}-url-toggle`}
          className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-terracotta"
        >
          {showUrl ? "Hide URL field" : "Paste a URL instead"}
        </button>
      </div>

      {showUrl && (
        <input
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          data-testid={`${testIdPrefix}-url-input`}
          className="mt-3 w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
          placeholder="https://…"
        />
      )}
    </div>
  );
}
